import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, of, catchError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import {
  canAccessFeatureFromRoles,
  DashboardFeature,
} from '../utils/feature-access.util';

/**
 * Requires a license tier that unlocks {@code feature}. Uses session roles first,
 * then refreshes from GET /users/me when the session has no roles yet.
 * Denied users land on dashboard billing.
 */
export function tierFeatureGuard(feature: DashboardFeature): CanActivateFn {
  return () => {
    const auth = inject(AuthService);
    const userService = inject(UserService);
    const router = inject(Router);
    const billingTree = router.createUrlTree(['/dashboard/billing']);

    const sessionRoles = auth.auth()?.user?.roles;
    if (sessionRoles && sessionRoles.length > 0) {
      return canAccessFeatureFromRoles(sessionRoles, feature) || billingTree;
    }

    return userService.getMe().pipe(
      map((user) => canAccessFeatureFromRoles(user.roles, feature) || billingTree),
      catchError(() => of(billingTree)),
    );
  };
}
